import React, { useCallback, useMemo } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { DropdownItem } from '@atlaskit/dropdown-menu';
import UploadIcon from '@atlaskit/icon/core/upload';

import { useContentTypeEventService } from '../ContentTypeEventServiceContext.tsx';
import { ACTION_SLIDES_EXPORT, ACTION_SLIDES_EXPORT_PDF } from '../moreActionsConstants.ts';

import {
	ExpandableNestedMenuItem,
	type ExpandableNestedMenuItemItemType,
} from './ExpandableNestedMenuItem.tsx';
import { i18n as sharedI18n, NestedMenuTypes } from './SharedActionMenuItems.tsx';

const i18n = defineMessages({
	slidesExportPdf: {
		id: 'content-types-header.ellipsis.open.ellipsis.slides.export.pdf.non-final',
		defaultMessage: 'Export to PDF',
		description:
			'The text shown as a sub-menu item under the Export menu for slides content type, representing the PDF export option.',
	},
});

type SlidesExportMenuItemProps = {
	handleNestedMenuItemClick: (type: NestedMenuTypes) => void;
	nestedPopup: NestedMenuTypes | undefined;
	handleNestedMenuItemClose: () => void;
	closeParentMenu: () => void;
};

/**
 * Slides-specific Export menu item. Renders a nested menu with format options.
 *
 * Each format option dispatches an 'export' command with a { format } payload
 * via the shared ContentTypeEventService, which the canvas slides app reads.
 */
export const SlidesExportMenuItem = ({
	handleNestedMenuItemClick,
	nestedPopup,
	handleNestedMenuItemClose,
	closeParentMenu,
}: SlidesExportMenuItemProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const intl = useIntl();
	// The event service is provided by WhiteboardShell via context.
	// We mount as 'confluence' (the AFM sender key) so commands are dispatched
	// TO canvas handlers registered under 'slides'.
	const eventService = useContentTypeEventService();
	const confluenceEventMount = useMemo(
		() => eventService.mountEventService('confluence'),
		[eventService],
	);

	const handlePdfClick = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'slidesExportPdf',
				source: 'overflowMenu',
			},
		}).fire();
		confluenceEventMount.sendCommand({
			type: 'export',
			payload: { product: 'slides', format: 'pdf' },
		});
		closeParentMenu();
	}, [createAnalyticsEvent, confluenceEventMount, closeParentMenu]);

	const slidesExportItems: ExpandableNestedMenuItemItemType[] = useMemo(
		() => [
			{
				id: ACTION_SLIDES_EXPORT_PDF,
				label: intl.formatMessage(i18n.slidesExportPdf),
				url: '',
				customComponent: () => (
					<DropdownItem
						key={ACTION_SLIDES_EXPORT_PDF}
						onClick={handlePdfClick}
						testId="slides-export-pdf"
					>
						<FormattedMessage {...i18n.slidesExportPdf} />
					</DropdownItem>
				),
			},
			// TODO: Add PowerPoint option here.
		],
		[intl, handlePdfClick],
	);

	const handleSlidesExportMenuItemClick = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'overflowSlidesExport',
				source: 'overflowMenu',
			},
		}).fire();
		handleNestedMenuItemClick(NestedMenuTypes.Export);
	}, [createAnalyticsEvent, handleNestedMenuItemClick]);

	return (
		<ExpandableNestedMenuItem
			id="slides-export"
			label={<FormattedMessage {...sharedI18n.export} />}
			items={slidesExportItems}
			iconBefore={<UploadIcon label="" />}
			isNestedMenuOpen={nestedPopup === NestedMenuTypes.Export}
			onClick={handleSlidesExportMenuItemClick}
			key={ACTION_SLIDES_EXPORT}
			onMenuClose={handleNestedMenuItemClose}
		/>
	);
};
